import React, { useEffect, useState } from 'react';
import Layout from '~/components/Layout';
import { useTranslation } from 'react-i18next';
import { Dropdown } from 'semantic-ui-react';
import dayjs from 'dayjs';
import FixedPriceChart from './FixedPriceChart';
import { StyledPriceContainer, StyledPriceWrapper } from './style';
import { capitalizeFirstLetter, formatNumber, getPrefixData, handleErrors } from '~/utils/helpers';
import { useAuth } from '~/routes/components/AuthProvider';
import { OPERATIVE_ROLE_SLUG } from '~/Constants';
import { useDebounce } from 'use-lodash-debounce';
import { FarmersService } from '~/api/services/farmers/index';
import FarmerGroupsService from '~/api/services/farmerGroups';
import { FixedPriceDataType, ReportingService } from '~/api/services/reporting';
import { useParams } from 'react-router-dom';
import { Nullable } from '~/types';

const FixedPrice: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const { systemData: { operativeUsersCanSeeFarmersInfo, logged: { roleSlug }, amountUnit, amountVolume, amountPrice } } = useAuth()
  const priceLabel = `${amountPrice == 'e' ? '€' : '€ Ct'}/${amountVolume > 1 ? amountVolume : ''}${amountUnit}`
  const { prefix } = useParams()

  const prefixData = getPrefixData(prefix, amountUnit)

  const [loadingFarmerGroupOptions, setLoadingFarmerGroupOptions] = useState(false)
  const [searchQueryFarmer, setSearchQueryFarmer] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const debouncedSearchValue = useDebounce(searchQuery, 300);
  const [farmerGroupsOptions, setFarmerGroupOptions] = useState([])
  const [loadingFarmersOptions, setLoadingFarmersOptions] = useState(false)
  const debouncedValueFarmer = useDebounce(searchQueryFarmer, 300)
  const [isBusy, setIsBusy] = useState(false)
  const [fixedPriceSettings, setFixedPriceSettings] = useState(null)
  const [fixedPriceData, setFixedPriceData] = useState<Nullable<FixedPriceDataType>>(null)
  const [farmersOptions, setFarmersOptions] = useState([])
  const [chartDomain, setChartDomain] = useState({
    dataMin: 0,
    dataMax: 0,
    divider: 0
  })
  const farmerFiltersDisabled = roleSlug === OPERATIVE_ROLE_SLUG && !operativeUsersCanSeeFarmersInfo
  const [dataFiltered, setDataFiltered] = useState({
    month: '',
    farmerGroupId: '',
    farmerId: ''
  })

  useEffect(() => {
    fetchFixedPriceData()
  }, [prefix])

  useEffect(() => {
    if (dataFiltered.month !== '') {
      fetchFilteredData()
    }
  }, [dataFiltered])

  async function fetchFixedPriceData() {
    setIsBusy(true)
    try {
      const response = await ReportingService(prefix).getFixedPriceSettings()
      setFixedPriceSettings(response.data)
      setDataFiltered({
        ...dataFiltered,
        month: response.data.selectedMonth
      })
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  async function fetchFilteredData() {
    setIsBusy(true)
    try {
      const response = await ReportingService(prefix).getFixedPriceData({ ...dataFiltered })
      const months = response.data?.tradingMonths
      const minPrice = Math.min(...months.map(item => parseFloat(item.price)))
      const maxPrice = Math.max(...months.map(item => parseFloat(item.price)))
      const minBase = Math.min(...months.map(item => parseFloat(item.basePrice)))
      const maxBase = Math.max(...months.map(item => parseFloat(item.basePrice)))
      const min = isNaN(minBase) ? minPrice : minPrice < minBase ? minPrice : minBase
      const max = isNaN(maxBase) ? maxPrice : maxPrice > maxBase ? maxPrice : maxBase
      const divider = Math.pow(10, Math.floor(max).toString().length - 2)
      setChartDomain({
        dataMin: Math.floor(min / divider) * divider,
        dataMax: Math.floor(max / divider) * divider,
        divider
      })
      setFixedPriceData(response.data)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  useEffect(() => {
    handleSearchFarmerGroups(searchQuery)
  }, [debouncedSearchValue]);

  useEffect(() => {
    handleSearchFarmers()
  }, [debouncedValueFarmer]);

  async function handleSearchFarmerGroups(searchQuery) {
    setLoadingFarmerGroupOptions(true)
    try {
      const response = await FarmerGroupsService.getFarmerGroupsList('milk_fixed_price_stock_exchange', searchQuery)
      const options = response.data.map(item => ({ value: item.id, text: item.name }))
      setFarmerGroupOptions(options)
      setLoadingFarmerGroupOptions(false)
    } catch (error) {
      setLoadingFarmerGroupOptions(false)
      handleErrors(error)
    }
  }

  async function handleSearchFarmers() {
    setLoadingFarmersOptions(true)
    try {
      const response = await FarmersService.getFarmers({ farmerGroupId: dataFiltered.farmerGroupId, search: searchQueryFarmer, page: 0 })
      const options = response.data.map(item => ({ value: item.id, text: item.companyName }))
      setFarmersOptions(options)
      setLoadingFarmersOptions(false)
    } catch (error) {
      setLoadingFarmersOptions(false)
      setFarmersOptions([])
      handleErrors(error)
    }
  }

  async function handleChangeFarmerGroup(id) {
    setLoadingFarmersOptions(true)
    setDataFiltered({
      ...dataFiltered,
      farmerGroupId: id,
      farmerId: ''
    })
    try {
      const responseFarmers = await FarmersService.getFarmers({ farmerGroupId: id, search: '', page: 0 })
      const optionsFarmers = responseFarmers.data.map(item => ({ value: item.id, text: item.companyName }))
      setFarmersOptions(optionsFarmers)
      setSearchQuery('')
    } catch (error) {
      handleErrors(error)
    }
    setLoadingFarmersOptions(false)
  }

  function renderDairyFilters() {
    return <>
      <Dropdown
        noResultsMessage={searchQuery !== '' ? t('No results found') : t('Please enter 1 or more character')}
        selection
        selectOnBlur={false}
        search={(options, query) => {
          return options
        }}
        clearable
        searchQuery={searchQuery}
        loading={loadingFarmerGroupOptions}
        options={farmerGroupsOptions}
        placeholder={t('Search farmer group')}
        value={dataFiltered.farmerGroupId}
        onSearchChange={(e, { searchQuery }) => setSearchQuery(searchQuery)}
        onChange={(e, data) => handleChangeFarmerGroup(data.value)}
      />
      <Dropdown
        noResultsMessage={searchQuery !== '' ? t('No results found') : t('Please enter 1 or more character')}
        selection
        selectOnBlur={false}
        search={(options, query) => {
          return options
        }}
        clearable
        searchQuery={searchQueryFarmer}
        loading={loadingFarmersOptions}
        options={farmersOptions}
        placeholder={t('Search farmers')}
        value={dataFiltered.farmerId}
        onSearchChange={(e, { searchQuery }) => setSearchQueryFarmer(searchQuery)}
        onChange={(e, data) => {
          setSearchQueryFarmer('')
          setDataFiltered({
            ...dataFiltered,
            farmerId: data.value as string
          })
        }}
      />
    </>
  }

  function renderFilters() {
    return <div style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 10 }}>
      <Dropdown
        selection
        onChange={(e, data) => setDataFiltered({
          ...dataFiltered,
          month: data.value as string
        })}
        label={t('Trading month')}
        placeholder={t('Trading month')}
        selectOnBlur={false}
        value={dataFiltered.month || fixedPriceSettings?.selectedMonth}
        options={fixedPriceSettings?.months?.map(month => ({ value: month, text: capitalizeFirstLetter(dayjs(month).format('MMM YYYY')) }))}
      />
      {!farmerFiltersDisabled && renderDairyFilters()}
    </div>
  }

  function renderChart() {
    return <>
      {dataFiltered.month !== '' && <FixedPriceChart
        fixedPriceData={fixedPriceData}
        basePrice={fixedPriceSettings?.text?.translation}
        chartDomain={chartDomain}
        prefixData={prefixData}
      />
      }
    </>
  }

  function renderBaseDataInfo() {
    const { priceTitle } = prefixData
    return <div style={{ marginBottom: 50 }}>
      <StyledPriceWrapper>
        <StyledPriceContainer>{t('Min {{price}}: {{maxPrice}} {{priceLabel}}',
          { maxPrice: fixedPriceData?.baseData?.minPrice ? formatNumber(fixedPriceData?.baseData?.minPrice) : 0, priceLabel, price: priceTitle })}
        </StyledPriceContainer>
        <StyledPriceContainer>{t('Max {{price}}: {{maxPrice}} {{priceLabel}}',
          { maxPrice: fixedPriceData?.baseData?.minPrice ? formatNumber(fixedPriceData?.baseData?.maxPrice) : 0, priceLabel, price: priceTitle })}
        </StyledPriceContainer>
      </StyledPriceWrapper>
      <StyledPriceWrapper>
        <StyledPriceContainer>{t('Average price: {{maxPrice}} {{priceLabel}}',
          { maxPrice: fixedPriceData?.baseData?.avgPrice ? formatNumber(fixedPriceData?.baseData?.avgPrice) : 0, priceLabel, price: priceTitle })}
        </StyledPriceContainer>
        <StyledPriceContainer>{t('Avg {{price}}: {{maxPrice}} {{priceLabel}}',
          { maxPrice: fixedPriceData?.baseData?.farmerAvgPrice ? formatNumber(fixedPriceData?.baseData?.farmerAvgPrice) : 0, priceLabel, price: priceTitle })}
        </StyledPriceContainer>
      </StyledPriceWrapper>
    </div>
  }

  return <Layout
    segmented
    isBusy={isBusy}
    filters={fixedPriceSettings && renderFilters()}
    title={prefixData.title}
  >
    {fixedPriceData?.baseData && renderBaseDataInfo()}
    {fixedPriceData && dataFiltered.month !== '' && renderChart()}
  </Layout>;
};

export default FixedPrice;
